import { Fragment, useMemo, useState } from "react";
import { Upload, ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import { money } from "@/lib/format";
import { WindowCarePanel } from "../WindowCarePanel";
import { isWindowCareScope } from "../windowCare";
import { AddLineItemButton } from "../AddLineItemButton";
import { ImportLinesDialog } from "./ImportLinesDialog";
import { GridScopeRow } from "./GridScopeRow";
import { GridLineRow } from "./GridLineRow";
import { LineDrawer } from "./LineDrawer";
import { Calc, GmCalc } from "./GridCell";
import { scopeRollup, colCount, gmPct } from "./gridMath";
import { findLibraryPart, libraryChanges } from "./libraryDiff";

const th = "th-std whitespace-nowrap px-1 py-1.5 border-b border-slate-200";
const thR = `${th} text-right`;
const grp = "border-b border-slate-200 px-1 py-1 text-center text-[10.5px] font-medium tracking-wide text-slate-400";

export function EstimateGrid({
  scopes, expandedScopes, toggleScope, toggleAllScopes, updateScope, updateLineItem, addLineItem, addLineItemFromPart, addLineItemsFromParts, removeLineItem, removeScope,
  duplicateLineItem, saveLineItemToLibrary, applyWindowCare, applyLibraryToLine, dismissLibraryForLine, addImportedLines,
  getPartsForScopeName, masterParts, partsStockMap, siteId, siteName, cols, reviewable, readOnly = false,
}) {
  const [selected, setSelected] = useState(null); // { scopeId, itemId }
  const [importScope, setImportScope] = useState(null);
  const N = colCount(cols);
  const allExpanded = scopes.length > 0 && scopes.every((s) => expandedScopes[s.id]);
  const rolls = useMemo(() => scopes.map(scopeRollup), [scopes]);
  const libInfo = useMemo(() => {
    const m = {};
    scopes.forEach((s) => (s.line_items || []).forEach((li) => {
      const part = findLibraryPart(li, masterParts);
      m[li.id] = { part, changes: libraryChanges(li, part) };
    }));
    return m;
  }, [scopes, masterParts]);
  const sum = (k) => rolls.reduce((a, r) => a + (r[k] || 0), 0);
  const grandTotal = sum("total"), grandCost = sum("cost");

  const sel = selected && (() => {
    const si = scopes.findIndex((s) => s.id === selected.scopeId);
    const ii = si >= 0 ? scopes[si].line_items.findIndex((li) => li.id === selected.itemId) : -1;
    return ii >= 0 ? { scope: scopes[si], si, item: scopes[si].line_items[ii], ii } : null;
  })();

  return (
    <div className="flex items-start gap-3">
      <div className="data-table-container min-w-0 flex-1 overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="estimate-grid">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className={`${grp} w-6 p-0`}></th>
              <th className={`${grp} text-left`} colSpan={2}>Item</th>
              <th className={`${grp} border-l`} colSpan={3}>Material</th>
              <th className={`${grp} border-l`} colSpan={3 + (cols.hrsTotal ? 1 : 0) + (cols.rate ? 1 : 0)}>Labor</th>
              <th className={`${grp} border-l`} colSpan={3 + (cols.freight ? 1 : 0) + (cols.stock ? 1 : 0)}>Price</th>
            </tr>
            <tr className="align-bottom">
              <th className={`${th} w-6 p-0 text-center`}>
                <button type="button" onClick={toggleAllScopes} title={allExpanded ? "Collapse all scopes" : "Expand all scopes"} aria-label={allExpanded ? "Collapse all scopes" : "Expand all scopes"}
                  className="mx-auto flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700" data-testid="btn-toggle-all-scopes">
                  {allExpanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
                </button>
              </th>
              <th className={`${th} text-left pl-2`}>Part # / Description</th>
              <th className={thR}>Qty</th>
              <th className={`${thR} border-l`}>Cost</th><th className={thR}>GM%</th><th className={thR}>$</th>
              <th className={`${thR} border-l`}>Hrs/Unit</th>{cols.hrsTotal && <th className={thR}>Total Hrs</th>}<th className={thR}>GM%</th>{cols.rate && <th className={thR}>$/hr</th>}<th className={thR}>$</th>
              {cols.freight && <th className={`${thR} border-l`}>Freight</th>}<th className={`${thR} ${cols.freight ? "" : "border-l"}`}>Unit</th><th className={thR}>Total</th><th className={thR}>GM</th>
              {cols.stock && <th className={`${thR} border-l`} title="On-hand stock from the Equipment Library (informational)">Stock</th>}
            </tr>
          </thead>
          <tbody>
            {scopes.map((scope, si) => {
              const expanded = !!expandedScopes[scope.id];
              const reviewCount = reviewable ? (scope.line_items || []).filter((li) => libInfo[li.id]?.changes.length).length : 0;
              return (
                <Fragment key={scope.id || `s-${si}`}>
                  <GridScopeRow scope={scope} scopeIdx={si} expanded={expanded} onToggle={() => toggleScope(scope.id)} roll={rolls[si]} reviewCount={reviewCount} cols={cols}
                    onRename={(v) => updateScope(scope.id, "name", v)} onRemove={() => removeScope(scope.id)} canRemove={scopes.length > 1} />
                  {expanded && isWindowCareScope(scope) && (
                    <tr><td colSpan={N} className="border-b border-slate-200 p-3"><WindowCarePanel scope={scope} scopeIdx={si} onChange={(cfg) => applyWindowCare(scope.id, cfg)} siteId={siteId} siteName={siteName} /></td></tr>
                  )}
                  {expanded && (scope.line_items || []).map((item, ii) => (
                    <GridLineRow key={item.id || `li-${si}-${ii}`} item={item} scopeIdx={si} itemIdx={ii} cols={cols} reviewable={reviewable}
                      update={(k, v) => updateLineItem(scope.id, item.id, k, v)}
                      selected={selected?.itemId === item.id} onSelect={(id) => setSelected((cur) => (cur?.itemId === id ? null : { scopeId: scope.id, itemId: id }))}
                      stock={item.part_number && partsStockMap[item.part_number] != null ? partsStockMap[item.part_number] : null}
                      changes={libInfo[item.id]?.changes || []}
                      onApplyLib={() => applyLibraryToLine(scope.id, item.id)} onDismissLib={() => dismissLibraryForLine(scope.id, item.id)} />
                  ))}
                  {expanded && !readOnly && (
                    <tr data-testid={`add-row-${si}`}><td className="border-b border-slate-200"></td><td colSpan={N - 1} className="border-b border-slate-200 px-2 py-1">
                      <div className="flex items-center gap-3">
                        <AddLineItemButton variant="link" scopeId={scope.id} scopeName={scope.name} scopeIdx={si} getPartsForScopeName={getPartsForScopeName}
                          addLineItemFromPart={addLineItemFromPart} addLineItemsFromParts={addLineItemsFromParts} addLineItem={addLineItem} allParts={masterParts} />
                        <span className="text-slate-300">·</span>
                        <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" onClick={() => setImportScope(scope)} data-testid={`btn-import-lines-${si}`}>
                          <Upload className="h-3 w-3" /> Quote
                        </button>
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-semibold" data-testid="grid-footer">
              <td className="h-10 border-t-2 border-slate-300"></td>
              <td className="h-10 border-t-2 border-slate-300 px-2 text-xs text-slate-700" colSpan={2}>
                Line Items
                {sum("subcontract") > 0 && <span className="ml-2 font-normal text-slate-500" data-testid="grid-footer-sub">· SUB {money(sum("subcontract"))}</span>}
              </td>
              <td className="border-l border-t-2 border-slate-300"></td><td className="border-t-2 border-slate-300"></td>
              <td className="border-t-2 border-slate-300 px-1"><Calc value={sum("material")} testId="grid-footer-material" /></td>
              <td className="border-l border-t-2 border-slate-300 px-1"><Calc kind="hours" value={sum("hours")} testId="grid-footer-hours" /></td>
              {cols.hrsTotal && <td className="border-t-2 border-slate-300"></td>}
              <td className="border-t-2 border-slate-300"></td>
              {cols.rate && <td className="border-t-2 border-slate-300"></td>}
              <td className="border-t-2 border-slate-300 px-1"><Calc value={sum("labor")} testId="grid-footer-labor" /></td>
              {cols.freight && <td className="border-l border-t-2 border-slate-300 px-1"><Calc value={sum("freight")} /></td>}
              <td className={`border-t-2 border-slate-300 ${cols.freight ? "" : "border-l"}`}></td>
              <td className="border-t-2 border-slate-300 px-1"><Calc strong value={grandTotal} testId="grid-footer-total" /></td>
              <td className="border-t-2 border-slate-300 px-1"><GmCalc value={gmPct(grandTotal, grandCost)} testId="grid-footer-gm" /></td>
              {cols.stock && <td className="border-l border-t-2 border-slate-300"></td>}
            </tr>
          </tfoot>
        </table>
      </div>
      {sel && (
        <LineDrawer item={sel.item} scopeIdx={sel.si} itemIdx={sel.ii} scopeName={sel.scope.name} reviewable={reviewable}
          update={(k, v) => updateLineItem(sel.scope.id, sel.item.id, k, v)} onClose={() => setSelected(null)}
          onDuplicate={() => duplicateLineItem(sel.scope.id, sel.item.id)}
          onRemove={() => { removeLineItem(sel.scope.id, sel.item.id); setSelected(null); }} canRemove={sel.scope.line_items.length > 1}
          onSaveLibrary={() => saveLineItemToLibrary(sel.scope, sel.item)}
          libPart={libInfo[sel.item.id]?.part} changes={libInfo[sel.item.id]?.changes || []}
          onApplyLib={() => applyLibraryToLine(sel.scope.id, sel.item.id)} onDismissLib={() => dismissLibraryForLine(sel.scope.id, sel.item.id)} />
      )}
      <ImportLinesDialog open={!!importScope} onClose={() => setImportScope(null)} scopeName={importScope?.name} masterParts={masterParts}
        onAdd={(items, meta) => addImportedLines(importScope.id, items, meta)} />
    </div>
  );
}
